import {
  MODAL_PROJECT_NOTIFICATIONS,
  MODAL_UPGRADE_PLAN,
} from '@components/App/Modal/constants'
import { useWorkspaces } from '@components/Dashboard/hooks'
import type { ID } from '@octopus-do/octopus-project-core'
import { DashboardActions } from '@store/reducers/dashboard/actions'
import { PERSONAL_WORKSPACE_ID } from '@store/reducers/dashboard/constants'
import type { ProjectItem, Workspace } from '@store/reducers/dashboard/types'
import { TemplatesActions } from '@store/reducers/templates'
import { useDispatch } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { OpenConfirmation } from '@/store'
import { openModal } from '@/store_old/reducers/ui'
import { createSwitch } from '@/utils'

const buildMovePath = (target: string[], current: Workspace | null) => {
  const sameWs = Boolean(current) && target.length === 1
  const currentId = current?.id || ''

  const folderId = sameWs ? target[0] : target[1]
  const workspaceId = sameWs ? currentId : target[0]

  return { folderId, workspaceId }
}

const useDashboardActions = (id: ID) => {
  const dispatch = useDispatch()

  return createSwitch()
    .case(['archive'], () =>
      dispatch(DashboardActions.projects.archive({ id }))
    )
    .case(['unarchive'], () =>
      dispatch(DashboardActions.projects.unarchive({ id }))
    )
    .case(['duplicate'], () =>
      dispatch(DashboardActions.projects.duplicate({ id }))
    )
    .case(['pin'], () => dispatch(DashboardActions.projects.pin({ id })))
    .case(['unpin'], () => dispatch(DashboardActions.projects.unpin({ id })))
}

// A template's delete goes through the templates slice, not the dashboard's:
// the template is not in any workspace folder, so the dashboard has nothing
// to remove.
const useTemplateActions = ({ id, title }: ProjectItem, workspaceId: ID) => {
  const dispatch = useDispatch()

  return createSwitch()
    .case(['use_template'], () =>
      dispatch(TemplatesActions.use({ id, workspaceId }))
    )
    .case(['delete'], () =>
      dispatch(
        OpenConfirmation({
          message: `Template «${title}» will be permanently deleted. Are you sure?`,
          label: 'Delete',
          title: 'Delete template',
          then: [TemplatesActions.delete({ id })],
        })
      )
    )
}

export const useProjectActions = (project: ProjectItem) => {
  const { id, title, template } = project
  const dispatch = useDispatch()
  const { current } = useWorkspaces()
  const dashboardSwitcher = useDashboardActions(id)
  const templateSwitcher = useTemplateActions(
    project,
    current?.id ?? PERSONAL_WORKSPACE_ID
  )

  if (template) {
    return { onClick: (path: string[]) => templateSwitcher.run(path) }
  }

  const switcher = createSwitch()
    .case(['transfer'], () =>
      // @ts-expect-error
      dispatch(openModal('MODAL_PROJECT_TRANSFER', { id }))
    )
    .case(['share'], () => dispatch(DashboardActions.projects.share({ id })))
    .case(['notifications'], () =>
      // @ts-expect-error
      dispatch(openModal(MODAL_PROJECT_NOTIFICATIONS, { id }))
    )
    .case(['archive_upgrade'], () => {
      sendGtmEvent(GtmTriggerName.UPGRADE_ARCHIVE)
      dispatch(openModal(MODAL_UPGRADE_PLAN))
    })
    .case(['unarchive_upgrade'], () => dispatch(openModal(MODAL_UPGRADE_PLAN)))
    .case(['delete'], () =>
      dispatch(
        OpenConfirmation({
          message: `Project «${title}» will be permanently deleted. Are you sure?`,
          label: 'Delete',
          title: 'Delete project',
          then: [DashboardActions.projects.delete({ id })],
        })
      )
    )
    .case(['leave'], () =>
      dispatch(
        OpenConfirmation({
          message: `You will be removed from the members of «${title}», and the project will no longer appear in your dashboard.`,
          label: 'Leave',
          title: 'Leave project',
          then: [DashboardActions.projects.leave({ id })],
        })
      )
    )
    .case(['move'], (...keys) => {
      const { workspaceId, folderId } = buildMovePath(keys, current)
      dispatch(
        DashboardActions.projects.moveToWorkspace({
          id,
          workspaceId,
          folderId,
        })
      )
    })
    .append(dashboardSwitcher)

  const onClick = (path: string[]) => switcher.run(path)

  return { onClick }
}
